import { useState, useEffect } from 'react';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { 
  Save, Percent, ChevronDown, ChevronUp, RefreshCw, AlertCircle, Building2, 
  Eye, EyeOff, Package, FolderOpen, Server, Cpu, HardDrive, Cloud, Globe, 
  Shield, Mail, Database, Zap, Lock 
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BILLING_CYCLES = [
  { key: 'monthly', label: '1 Month', shortLabel: '1M', months: 1 },
  { key: 'quarterly', label: '3 Months', shortLabel: '3M', months: 3 },
  { key: 'half_yearly', label: '6 Months', shortLabel: '6M', months: 6 },
  { key: 'yearly', label: '12 Months', shortLabel: '12M', months: 12 },
  { key: 'two_years', label: '24 Months', shortLabel: '24M', months: 24 },
  { key: 'three_years', label: '36 Months', shortLabel: '36M', months: 36 }
];

// Icon mapping for categories
const CATEGORY_ICONS = {
  'Server': Server,
  'Cpu': Cpu,
  'HardDrive': HardDrive,
  'Cloud': Cloud,
  'Globe': Globe,
  'Shield': Shield,
  'Mail': Mail,
  'Database': Database,
  'Zap': Zap,
  'Lock': Lock,
  'Package': Package
};

export default function ResellerPricing() {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedProducts, setExpandedProducts] = useState({});
  const [bulkMarkup, setBulkMarkup] = useState('');
  const [bulkMarkupType, setBulkMarkupType] = useState('percentage'); // 'percentage' | 'fixed'
  const [customPrices, setCustomPrices] = useState({});
  const [productVisibility, setProductVisibility] = useState({});
  const [categoryVisibility, setCategoryVisibility] = useState({});
  const [savingProduct, setSavingProduct] = useState(null);
  const [savingCategory, setSavingCategory] = useState(null);
  const [applyingBulk, setApplyingBulk] = useState(false);
  const [parentInfo, setParentInfo] = useState(null);
  const [priceErrors, setPriceErrors] = useState({});
  const [pricing, setPricing] = useState([]); // Keep for price calculations

  useEffect(() => {
    fetchCategoriesWithProducts();
    fetchPricing();
  }, []);

  const fetchCategoriesWithProducts = async () => {
    try {
      const response = await axios.get(`${API}/reseller/categories`);
      setCategories(response.data);
      
      // Initialize visibility states
      const catVis = {};
      const prodVis = {};
      const expanded = {};
      
      response.data.forEach(cat => {
        catVis[cat.id] = cat.is_visible;
        expanded[cat.id] = false; // Start collapsed
        
        if (cat.products) {
          cat.products.forEach(prod => {
            prodVis[prod.id] = prod.is_visible;
          });
        }
      });
      
      setCategoryVisibility(catVis);
      setProductVisibility(prodVis);
      setExpandedCategories(expanded);
    } catch (error) {
      toast.error('Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  };

  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${API}/reseller/pricing`);
      setPricing(response.data);
      
      // Get parent info if Channel Partner
      if (response.data.length > 0 && response.data[0].parent_info) {
        setParentInfo(response.data[0].parent_info);
      }
      
      const prices = {};
      
      response.data.forEach(item => {
        const productId = item.product.id;
        const basePrices = item.effective_base_prices || item.product.billing_prices || {};
        
        if (item.custom_pricing && item.custom_pricing.custom_prices) {
          prices[productId] = item.custom_pricing.custom_prices;
        } else {
          prices[productId] = {
            add: { ...basePrices },
            renew: { ...basePrices }
          };
        }
      });
      
      setCustomPrices(prices);
    } catch (error) {
      console.log('Failed to fetch pricing');
    }
  };

  const handleCategoryVisibilityToggle = async (categoryId, isVisible) => {
    setSavingCategory(categoryId);
    try {
      await axios.post(`${API}/reseller/category-visibility?category_id=${categoryId}&is_visible=${isVisible}`);
      setCategoryVisibility(prev => ({ ...prev, [categoryId]: isVisible }));
      toast.success(`Category ${isVisible ? 'shown' : 'hidden'} successfully`);
      // Refresh to get updated hierarchy
      fetchCategoriesWithProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update category visibility');
    } finally {
      setSavingCategory(null);
    }
  };

  const handleProductVisibilityToggle = async (productId, isVisible) => {
    try {
      await axios.post(`${API}/reseller/product-visibility?product_id=${productId}&is_visible=${isVisible}`);
      setProductVisibility(prev => ({ ...prev, [productId]: isVisible }));
      toast.success(`Product ${isVisible ? 'shown' : 'hidden'} successfully`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update product visibility');
    }
  };

  const handlePriceChange = (productId, type, cycle, value, costPrice) => {
    let numValue;
    if (value === '' || value === null || value === undefined) {
      numValue = '';
    } else {
      const parsed = parseFloat(value);
      numValue = isNaN(parsed) ? '' : parsed;
    }
    
    const errorKey = `${productId}-${type}-${cycle}`;
    if (typeof numValue === 'number' && numValue < costPrice && numValue > 0) {
      setPriceErrors(prev => ({
        ...prev,
        [errorKey]: `Price cannot be below cost (₹${costPrice.toLocaleString()})`
      }));
    } else {
      setPriceErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
    
    setCustomPrices(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        [type]: {
          ...(prev[productId]?.[type] || {}),
          [cycle]: numValue
        }
      }
    }));
  };

  const handleSaveProduct = async (productId) => {
    const productErrors = Object.keys(priceErrors).filter(key => key.startsWith(productId));
    if (productErrors.length > 0) {
      toast.error('Please fix price errors before saving. Prices cannot be below cost.');
      return;
    }
    
    const item = pricing.find(p => p.product.id === productId);
    if (!item) return;
    
    const basePrices = getEffectiveBasePrices(item);
    const productPrices = customPrices[productId] || { add: {}, renew: {} };
    
    const finalPrices = { add: {}, renew: {} };
    
    for (const cycle of BILLING_CYCLES) {
      const costPrice = basePrices[cycle.key] || 0;
      const addPrice = productPrices?.add?.[cycle.key];
      const renewPrice = productPrices?.renew?.[cycle.key];
      
      finalPrices.add[cycle.key] = (addPrice === '' || addPrice === 0 || addPrice === undefined) ? costPrice : addPrice;
      finalPrices.renew[cycle.key] = (renewPrice === '' || renewPrice === 0 || renewPrice === undefined) ? costPrice : renewPrice;
      
      if (finalPrices.add[cycle.key] < costPrice) {
        toast.error(`Add price for ${cycle.label} cannot be below cost (₹${costPrice.toLocaleString()})`);
        return;
      }
      if (finalPrices.renew[cycle.key] < costPrice) {
        toast.error(`Renew price for ${cycle.label} cannot be below cost (₹${costPrice.toLocaleString()})`);
        return;
      }
    }
    
    setSavingProduct(productId);
    
    try {
      await axios.post(`${API}/reseller/pricing`, {
        product_id: productId,
        custom_prices: finalPrices,
        is_visible: productVisibility[productId] ?? true
      });
      toast.success('Pricing saved successfully');
      fetchPricing();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save pricing');
    } finally {
      setSavingProduct(null);
    }
  };

  const handleBulkApply = async () => {
    const value = parseFloat(bulkMarkup);
    if (isNaN(value) || value < 0) {
      toast.error(`Please enter a valid ${bulkMarkupType === 'fixed' ? 'amount' : 'percentage'}`);
      return;
    }

    setApplyingBulk(true);
    try {
      const payload =
        bulkMarkupType === 'fixed'
          ? { markup_type: 'fixed', fixed_markup: value, apply_to_all: true }
          : { markup_type: 'percentage', markup_percentage: value, apply_to_all: true };

      await axios.post(`${API}/reseller/pricing/bulk`, payload);
      const label = bulkMarkupType === 'fixed' ? `₹${value} fixed` : `${value}%`;
      toast.success(`Applied ${label} markup to all products`);
      setBulkMarkup('');
      fetchPricing();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply bulk pricing');
    } finally {
      setApplyingBulk(false);
    }
  };

  const calculateMarkup = (basePrice, sellingPrice) => {
    if (!basePrice || !sellingPrice || basePrice === 0) return 0;
    return (((sellingPrice - basePrice) / basePrice) * 100).toFixed(1);
  };

  const toggleCategoryExpand = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const toggleProductExpand = (productId) => {
    setExpandedProducts(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };

  const getEffectiveBasePrices = (item) => {
    return item.effective_base_prices || item.product.billing_prices || {
      monthly: item.product.base_price,
      quarterly: item.product.base_price * 3,
      half_yearly: item.product.base_price * 6,
      yearly: item.product.base_price * 12,
      two_years: item.product.base_price * 24,
      three_years: item.product.base_price * 36
    };
  };

  const getPricingItem = (productId) => {
    return pricing.find(p => p.product.id === productId);
  };

  const getCategoryIcon = (iconName) => {
    const IconComponent = CATEGORY_ICONS[iconName] || Package;
    return IconComponent;
  };

  const isChannelPartner = user?.role === 'sub_reseller';

  return (
    <DashboardLayout role={user?.role}>
      <div className="space-y-6">
        <div className="mb-2">
          <h1 className="text-4xl font-bold tracking-tight" data-testid="pricing-page-title">Product Pricing</h1>
          <p className="text-slate-500 mt-2">Manage your product pricing by category</p>
        </div>

        {/* Channel Partner Cost Source Info */}
        {isChannelPartner && parentInfo && (
          <Card className="border-purple-200 bg-purple-50/50" data-testid="parent-info-card">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-purple-700 font-medium">Your Cost Prices from Synergy Partner</p>
                  <p className="text-purple-900 font-semibold">{parentInfo.company_name}</p>
                  <p className="text-xs text-purple-600">{parentInfo.email}</p>
                </div>
              </div>
              <p className="text-xs text-purple-600 mt-3 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                Your selling prices must be equal to or higher than the cost prices. Hidden categories/products by your Synergy Partner are not visible here.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Bulk Markup Section */}
        <Card className="border-blue-200 bg-blue-50/50" data-testid="bulk-markup-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Percent className="h-5 w-5 text-blue-600" />
              Bulk Markup - Apply to All Products
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
              <div className="flex-1 space-y-3">
                {/* Markup type toggle */}
                <div className="flex gap-1 p-1 bg-slate-100 rounded-lg w-fit">
                  <button
                    onClick={() => { setBulkMarkupType('percentage'); setBulkMarkup(''); }}
                    data-testid="markup-type-percentage"
                    className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${
                      bulkMarkupType === 'percentage'
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    % Percentage
                  </button>
                  <button
                    onClick={() => { setBulkMarkupType('fixed'); setBulkMarkup(''); }}
                    data-testid="markup-type-fixed"
                    className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${
                      bulkMarkupType === 'fixed'
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    ₹ Fixed Amount
                  </button>
                </div>

                {/* Input + Apply */}
                <div className="flex gap-2">
                  <div className="relative max-w-xs">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-medium select-none pointer-events-none">
                      {bulkMarkupType === 'fixed' ? '₹' : '%'}
                    </span>
                    <Input
                      id="bulk-markup"
                      type="number"
                      step={bulkMarkupType === 'fixed' ? '1' : '0.1'}
                      min="0"
                      value={bulkMarkup}
                      onChange={(e) => setBulkMarkup(e.target.value)}
                      placeholder={bulkMarkupType === 'fixed' ? 'e.g., 50' : 'e.g., 10'}
                      className="pl-8"
                      data-testid="bulk-markup-input"
                    />
                  </div>
                  <Button
                    onClick={handleBulkApply}
                    disabled={applyingBulk || !bulkMarkup}
                    data-testid="apply-bulk-btn"
                  >
                    {applyingBulk ? (
                      <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Applying...</>
                    ) : (
                      'Apply to All'
                    )}
                  </Button>
                </div>
                <p className="text-xs text-slate-500">
                  {bulkMarkupType === 'fixed'
                    ? 'Adds a fixed ₹ amount on top of the base price for every billing cycle.'
                    : 'Applies the same % markup on the base price for every billing cycle.'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Categories Accordion */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : categories.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <FolderOpen className="h-12 w-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No categories available</p>
              <p className="text-sm text-slate-400 mt-2">Contact your administrator to set up product categories</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4 grid gap-2 mt-4  overflow-x-auto">
            {categories.map((category) => {
              const IconComponent = getCategoryIcon(category.icon);
              const isExpanded = expandedCategories[category.id];
              const isCategoryVisible = categoryVisibility[category.id] ?? category.is_visible;
              const isParentHidden = isChannelPartner && category.parent_visible === false;
              const products = category.products || [];
              const visibleProductCount = products.filter(p => productVisibility[p.id] ?? p.is_visible).length;
              
              return (
                <Card 
                  key={category.id} 
                  data-testid={`category-card-${category.id}`}
                  className={`transition-all ${!isCategoryVisible ? 'opacity-60 border-dashed' : ''} ${isParentHidden ? 'opacity-40 bg-slate-50' : ''}`}
                >
                  <CardHeader className="p-6">
                    <div className="flex items-start justify-between">
                      <div 
                        className="flex items-start gap-3 cursor-pointer flex-1"
                        onClick={() => toggleCategoryExpand(category.id)}
                      >
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 bg-blue-600 justify-center text-white text-center inline-flex items-center hover:bg-blue-700"
                        >
                          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </Button>
                        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${category.color || 'bg-blue-100 text-blue-700'}`}>
                          <IconComponent className="h-5 w-5" />
                        </div>
                        <div className="w-full mr-3">
                          <CardTitle className="text-lg flex items-center gap-2">
                            {category.name}
                            {!isCategoryVisible && (
                              <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded">Hidden</span>
                            )}
                            {isParentHidden && (
                              <span className="text-xs bg-purple-200 text-purple-700 px-2 py-0.5 rounded">Hidden by Synergy Partner</span>
                            )}
                          </CardTitle>
                          <p className="text-sm text-slate-500">
                            {products.length} product{products.length !== 1 ? 's' : ''} 
                            {visibleProductCount !== products.length && ` (${visibleProductCount} visible)`}
                          </p> 
                             {/* Description */}
                            {category.description && (
                              <div className="mt-1 w-full">
                                <p className="text-sm text-slate-600 leading-6 break-words">
                                  {category.description}
                                </p>
                              </div>
                            )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        {/* Category visibility toggle — shows CURRENT state */}
                        {!isParentHidden && (
                          <button
                            onClick={() => handleCategoryVisibilityToggle(category.id, !isCategoryVisible)}
                            disabled={savingCategory === category.id}
                            className={`text-xs font-semibold px-3 py-1 rounded-full border transition-colors disabled:opacity-50 ${
                              isCategoryVisible
                                ? 'bg-green-100 text-green-700 border-green-300 hover:bg-green-200'
                                : 'bg-red-100 text-red-700 border-red-300 hover:bg-red-200'
                            }`}
                            data-testid={`category-visibility-${category.id}`}
                            title={isCategoryVisible ? 'Category is visible — click to hide' : 'Category is hidden — click to show'}
                          >
                            {isCategoryVisible ? 'SHOW' : 'HIDE'}
                          </button>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  
                  {isExpanded && (
                    <CardContent className="pt-0 pb-4 border-t">
                      {products.length === 0 ? (
                        <p className="text-sm text-slate-500 py-4 text-center">No products in this category</p>
                      ) : (
                        <div className="space-y-3 mt-4">
                          {products.map((product) => {
                            const pricingItem = getPricingItem(product.id);
                            const basePrices = pricingItem ? getEffectiveBasePrices(pricingItem) : {};
                            const isProductExpanded = expandedProducts[product.id];
                            const productPrices = customPrices[product.id] || { add: {}, renew: {} };
                            const hasErrors = Object.keys(priceErrors).some(key => key.startsWith(product.id));
                            const isProductVisible = productVisibility[product.id] ?? product.is_visible;
                            const isProductParentHidden = isChannelPartner && product.parent_visible === false;
                            
                            return (
                              <Card 
                                key={product.id} 
                                data-testid={`product-card-${product.id}`} 
                                className={`border-slate-200 ${hasErrors ? 'border-red-300' : ''} ${!isProductVisible ? 'opacity-60 border-dashed' : ''} ${isProductParentHidden ? 'opacity-40 bg-slate-50' : ''}  mb-2`}
                              >
                                <CardHeader className="py-3 px-4">
                                  <div className="flex items-end justify-between">
                                    <div 
                                      className="flex items-start gap-3 cursor-pointer flex-1"
                                      onClick={() => toggleProductExpand(product.id)}
                                    >
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0 bg-blue-500 text-white"
                                      >
                                        {isProductExpanded ? <ChevronUp className="h-4 w-4 text-white" /> : <ChevronDown className="h-4 w-4" />}
                                      </Button>{/* Description + Features */}
                                      <div className="flex-1 min-w-0 mr-3">
                                        {/* Product Information */}
                                        <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 sm:p-5 w-full">

                                          {/* Product Name + Cost */}
                                          <div>
                                            <p className="text-sm sm:text-base font-semibold text-slate-900">
                                              {product.name}

                                              {!isProductVisible && (
                                                <span className="ml-2 text-[10px] sm:text-xs bg-slate-200 text-slate-600 px-2 py-1 rounded-md">
                                                  Hidden
                                                </span>
                                              )}

                                              {isProductParentHidden && (
                                                <span className="ml-2 text-[10px] sm:text-xs bg-purple-200 text-purple-700 px-2 py-1 rounded-md">
                                                  Hidden by SP
                                                </span>
                                              )}
                                            </p>

                                            <p className="text-xs sm:text-sm text-slate-500 mt-1">
                                              Cost: ₹{basePrices.monthly?.toLocaleString() || 'N/A'}/mo
                                              {product.pricing_unit === 'per_user' && <span className="ml-1 text-xs bg-blue-50 text-blue-600 border border-blue-200 rounded px-1.5 py-0.5">per user</span>}
                                              {product.pricing_unit === 'per_account' && <span className="ml-1 text-xs bg-purple-50 text-purple-600 border border-purple-200 rounded px-1.5 py-0.5">per account</span>}
                                            </p>
                                          </div>

                                          {/* Description */}
                                          {product.description && (
                                            <div className="mt-2">
                                              <p className="text-sm text-slate-600 leading-6 break-words">
                                                {product.description}
                                              </p>
                                            </div>
                                          )}

                                          {/* Features */}
                                          {product.features && (
                                            <div className="mt-4 pt-4 border-t border-slate-200">

                                              <p className="text-xs font-semibold text-slate-700 mb-3">
                                                Features
                                              </p>

                                              <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-x-6 gap-y-2">
                                                {Array.isArray(product.features)
                                                  ? product.features.map((feature, index) => (
                                                      <div
                                                        key={index}
                                                        className="flex items-start gap-2 text-sm text-slate-600"
                                                      >
                                                        <span className="text-cyan-600 font-semibold shrink-0">
                                                          ✓
                                                        </span>

                                                        <span className="break-words">
                                                          {feature}
                                                        </span>
                                                      </div>
                                                    ))
                                                  : product.features
                                                      .split('\n')
                                                      .filter(Boolean)
                                                      .map((feature, index) => (
                                                        <div
                                                          key={index}
                                                          className="flex items-start gap-2 text-sm text-slate-600"
                                                        >
                                                          <span className="text-cyan-600 font-semibold shrink-0">
                                                            ✓
                                                          </span>

                                                          <span className="break-words">
                                                            {feature.trim()}
                                                          </span>
                                                        </div>
                                                      ))}
                                              </div>

                                            </div>
                                          )}

                                        </div>

                                      </div>
                                    </div>
                                    
                                    <div className="flex items-center gap-3">
                                      {hasErrors && (
                                        <span className="text-xs text-red-600 flex items-center gap-1">
                                          <AlertCircle className="h-3 w-3" />
                                          Below cost
                                        </span>
                                      )}
                                      
                                      {/* Product visibility toggle — shows CURRENT state */}
                                      {!isProductParentHidden && (
                                        <button
                                          onClick={() => handleProductVisibilityToggle(product.id, !isProductVisible)}
                                          className={`text-xs font-semibold px-3 py-1 rounded-full border transition-colors ${
                                            isProductVisible
                                              ? 'bg-green-100 text-green-700 border-green-300 hover:bg-green-200'
                                              : 'bg-red-100 text-red-700 border-red-300 hover:bg-red-200'
                                          }`}
                                          data-testid={`product-visibility-${product.id}`}
                                          title={isProductVisible ? 'Product is visible — click to hide' : 'Product is hidden — click to show'}
                                        >
                                          {isProductVisible ? 'SHOW' : 'HIDE'}
                                        </button>
                                      )}
                                      
                                      <Button
                                        size="sm"
                                        onClick={() => handleSaveProduct(product.id)}
                                        disabled={savingProduct === product.id}
                                        data-testid={`save-product-${product.id}`}
                                      >
                                        {savingProduct === product.id ? (
                                          <RefreshCw className="h-4 w-4 animate-spin" />
                                        ) : (
                                          <Save className="h-4 w-4" />
                                        )}
                                      </Button>
                                    </div>
                                  </div>
                                </CardHeader>
                                
                                {isProductExpanded && pricingItem && (
                                  <CardContent className="pt-0 px-4 pb-4">
                                    <div className="overflow-x-auto">
                                      <table className="w-full text-sm">
                                        <thead>
                                          <tr className="border-b bg-slate-50">
                                            <th className="text-left py-2 px-3 font-medium text-xs">Cycle</th>
                                            <th className="text-center py-2 px-3 font-medium text-xs bg-slate-100">
                                              {isChannelPartner ? 'Cost (SP)' : 'Cost'}
                                            </th>
                                            <th className="text-center py-2 px-3 font-medium text-xs bg-green-50">Add Price</th>
                                            <th className="text-center py-2 px-3 font-medium text-xs bg-blue-50">Renew Price</th>
                                            <th className="text-center py-2 px-3 font-medium text-xs">Markup</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {BILLING_CYCLES.filter(cycle =>
                                            !product.enabled_billing_cycles || product.enabled_billing_cycles.length === 0 || product.enabled_billing_cycles.includes(cycle.key)
                                          ).map((cycle) => {
                                            const costPrice = basePrices[cycle.key] || 0;
                                            const rawAddPrice = productPrices.add?.[cycle.key];
                                            const rawRenewPrice = productPrices.renew?.[cycle.key];
                                            const addPrice = rawAddPrice === undefined ? costPrice : rawAddPrice;
                                            const renewPrice = rawRenewPrice === undefined ? costPrice : rawRenewPrice;
                                            const addMarkup = calculateMarkup(costPrice, addPrice || 0);
                                            const renewMarkup = calculateMarkup(costPrice, renewPrice || 0);
                                            const addErrorKey = `${product.id}-add-${cycle.key}`;
                                            const renewErrorKey = `${product.id}-renew-${cycle.key}`;
                                            const hasAddError = priceErrors[addErrorKey];
                                            const hasRenewError = priceErrors[renewErrorKey];
                                            
                                            return (
                                              <tr key={cycle.key} className="border-b hover:bg-slate-50">
                                                <td className="py-2 px-3">
                                                  <p className="font-medium text-xs">{cycle.shortLabel}</p>
                                                </td>
                                                <td className="py-2 px-3 text-center bg-slate-50">
                                                  <p className="font-semibold text-xs text-slate-700">
                                                    ₹{costPrice.toLocaleString()}
                                                  </p>
                                                </td>
                                                <td className="py-2 px-3 bg-green-50/50">
                                                  <div className="space-y-1">
                                                    <Input
                                                      type="number"
                                                      step="0.01"
                                                      min={costPrice}
                                                      value={addPrice === '' ? '' : addPrice}
                                                      onChange={(e) => handlePriceChange(product.id, 'add', cycle.key, e.target.value, costPrice)}
                                                      className={`w-24 mx-auto text-center text-xs h-8 ${hasAddError ? 'border-red-500' : ''}`}
                                                      data-testid={`add-price-${product.id}-${cycle.key}`}
                                                    />
                                                    {hasAddError && <p className="text-xs text-red-500 text-center">Below cost!</p>}
                                                  </div>
                                                </td>
                                                <td className="py-2 px-3 bg-blue-50/50">
                                                  <div className="space-y-1">
                                                    <Input
                                                      type="number"
                                                      step="0.01"
                                                      min={costPrice}
                                                      value={renewPrice === '' ? '' : renewPrice}
                                                      onChange={(e) => handlePriceChange(product.id, 'renew', cycle.key, e.target.value, costPrice)}
                                                      className={`w-24 mx-auto text-center text-xs h-8 ${hasRenewError ? 'border-red-500' : ''}`}
                                                      data-testid={`renew-price-${product.id}-${cycle.key}`}
                                                    />
                                                    {hasRenewError && <p className="text-xs text-red-500 text-center">Below cost!</p>}
                                                  </div>
                                                </td>
                                                <td className="py-2 px-3 text-center">
                                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                                    parseFloat(addMarkup) > 0 ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-600'
                                                  }`}>
                                                    +{addMarkup}%
                                                  </span>
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  </CardContent>
                                )}
                                
                                {/* Collapsed summary */}
                                {!isProductExpanded && pricingItem && (
                                  <CardContent className="pt-0 px-4 pb-3">
                                    {(() => {
                                      const enabledCycles = product.enabled_billing_cycles && product.enabled_billing_cycles.length > 0
                                        ? product.enabled_billing_cycles
                                        : ['monthly'];
                                      const firstKey = BILLING_CYCLES.find(c => enabledCycles.includes(c.key))?.key || 'monthly';
                                      const firstLabel = BILLING_CYCLES.find(c => c.key === firstKey)?.shortLabel || '1M';
                                      return (
                                        <div className="flex flex-wrap gap-4 text-xs">
                                          <span className="text-slate-500">
                                            Add: <span className="font-semibold text-green-600">₹{productPrices.add?.[firstKey]?.toLocaleString() || basePrices[firstKey]?.toLocaleString()}</span>/{firstLabel}
                                          </span>
                                          <span className="text-slate-500">
                                            Renew: <span className="font-semibold text-blue-600">₹{productPrices.renew?.[firstKey]?.toLocaleString() || basePrices[firstKey]?.toLocaleString()}</span>/{firstLabel}
                                          </span>
                                        </div>
                                      );
                                    })()}
                                  </CardContent>
                                )}
                              </Card>
                            );
                          })}
                        </div>
                      )}
                    </CardContent>
                  )}
                </Card>
              );
            })}
          </div>
        )}

        {/* Pricing Guidelines */}
        <Card>
          <CardContent className="p-6">
            <h3 className="font-semibold mb-2">Pricing Guidelines</h3>
            <ul className="text-sm text-slate-600 space-y-1">
              <li>• <strong>Category visibility</strong>: Hide an entire category to hide all its products from your clients</li>
              <li>• <strong>Product visibility</strong>: Hide individual products within a visible category</li>
              <li>• Your selling price must be equal to or higher than your cost price</li>
              <li>• Use the <strong>Bulk Markup</strong> feature to apply the same percentage to all products</li>
              <li>• You can set different prices for <strong>Add</strong> (new orders) and <strong>Renew</strong> (renewals)</li>
              {isChannelPartner && (
                <li className="text-purple-600">• Categories/products hidden by your Synergy Partner are not shown here and cannot be unhidden</li>
              )}
            </ul>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
